import { LanguagesEnum } from '@/api/enums';
import {
  InteractiveElementType,
  Slide,
  SlideTemplateType,
  TextAlignment,
} from '@/client/courses/types/courses.type';
import { ContentEmailComponent } from '@/components/courses/elements/ContentEmailComponent';
import { ContentSMSComponent } from '@/components/courses/elements/ContentSMSComponent';
import { EditableText } from '@/ui/editable-text/EditableText';
import { InfoBox } from '@/ui/info-box';
import { RichTextEditorHandle, RichTextVendorEditor } from '@/ui/rich-text';
import { FlexContainer } from '@/ui/styled-ui';
import { filterSlideImagesByTemplate } from '@/utils/helpers/courses.helper';
import { isRtlLanguage } from '@/utils/helpers/language.helper';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SlideImageComponent } from '../../elements/SlideImageComponent';
import { RichTextArea } from './RichTextArea';
import { RichTextTitle } from './RichTextTitle';

const SlideImagePreview = styled.div`
  width: 300px !important;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;

  .p-image {
    height: 100%;
    display: flex;
  }

  img {
    width: 100%;
    height: auto;
    object-fit: cover !important;
  }

  button {
    border-radius: var(--medium-border-radius);
  }
`;

const Footnote = styled.div`
  line-height: 1;
  font-size: var(--xsmall-font-size);
  margin-bottom: var(--small-padding);
  margin-top: var(--medium-padding);
`;

interface TextImageFormProps {
  currentEditingSlide: Slide;
  currentSlideData: Slide;
  selectedLanguage: string;
  isEditingPopUpSlide: boolean;
  currentSlideIdx: number;
  slides: Slide[];
  totalSlides: number;
  updateSlide: (id: string, updates: Partial<Slide>) => void;
  registerEditor: (handle: RichTextEditorHandle | null) => void;
  vendorEditorRef: React.RefObject<RichTextVendorEditor>;
  isSummary?: boolean;
}

export const TextImageForm: React.FC<TextImageFormProps> = ({
  currentEditingSlide,
  updateSlide,
  registerEditor,
  vendorEditorRef,
  selectedLanguage,
  isEditingPopUpSlide,
  currentSlideIdx,
  slides,
  currentSlideData,
  isSummary,
}) => {
  const { t } = useTranslation();

  const getPopupSlideId = (slide: Slide): string | undefined => {
    if (slide.popupSlide) {
      return typeof slide.popupSlide === 'string'
        ? slide.popupSlide
        : slide.popupSlide.id;
    }
    return slide.tempPopupSlideId;
  };

  const getPopupSlideContent = () => {
    if (!currentSlideData.popupSlide && !currentSlideData.tempPopupSlideId) {
      return;
    }

    const popupId = getPopupSlideId(currentSlideData);
    if (!popupId) return;

    const linkedSlide = slides.find(
      ({ id, tempId }) => id === popupId || tempId === popupId,
    );
    if (!linkedSlide) return;

    return linkedSlide;
  };

  return (
    <FlexContainer gap={22} justify="space-between" align="flex-start">
      <div style={{ flexGrow: 1 }}>
        <RichTextTitle
          currentEditingSlide={currentEditingSlide}
          isEditingPopUpSlide={isEditingPopUpSlide}
          currentSlideData={currentSlideData}
          updateSlide={updateSlide}
          isPurpleBackground={isSummary}
        />

        <RichTextArea
          currentEditingSlide={currentEditingSlide}
          isEditingPopUpSlide={isEditingPopUpSlide}
          selectedLanguage={selectedLanguage}
          currentSlideIdx={currentSlideIdx}
          currentSlideData={currentSlideData}
          updateSlide={updateSlide}
          registerEditor={registerEditor}
          vendorEditorRef={vendorEditorRef}
          isPurpleBackground={isSummary}
        />

        {!isEditingPopUpSlide && currentSlideData.infobox && (
          <InfoBox
            text={currentSlideData.infobox}
            modalContent={getPopupSlideContent()}
            isSummary={isSummary}
          />
        )}
        {!isEditingPopUpSlide && (
          <Footnote>
            <EditableText
              key={`${selectedLanguage}-${
                currentEditingSlide?.id
              }-${currentSlideIdx}-${isEditingPopUpSlide ? 'popup' : 'main'}`}
              multiline
              value={currentEditingSlide?.footnote || ''}
              placeholder={t('content.slide.footnote.placeholder')}
              showTooltip={false}
              textAlignment={currentSlideData.textAlignment}
              saveValue={(value: string) => {
                if (currentEditingSlide?.id) {
                  updateSlide(currentEditingSlide.id, {
                    footnote: value,
                  });
                }
              }}
              isPurpleBackground={isSummary}
            />
          </Footnote>
        )}
      </div>

      {currentSlideData.interactiveElement ? (
        <div>
          {currentSlideData.interactiveElement.type ===
          InteractiveElementType.EMAIL ? (
            <ContentEmailComponent
              id={currentSlideData.id}
              interactiveElement={currentSlideData.interactiveElement}
              onUpdate={(data) => {
                if (currentEditingSlide?.id) {
                  updateSlide(currentEditingSlide.id, {
                    interactiveElement: {
                      ...currentSlideData.interactiveElement,
                      data,
                    },
                  });
                }
              }}
            />
          ) : currentSlideData.interactiveElement.type ===
            InteractiveElementType.SMS ? (
            <ContentSMSComponent
              id={currentSlideData.id}
              interactiveElement={currentSlideData.interactiveElement}
              rtl={isRtlLanguage(selectedLanguage as LanguagesEnum)}
              onUpdate={(data) => {
                if (currentEditingSlide?.id) {
                  updateSlide(currentEditingSlide.id, {
                    interactiveElement: {
                      ...currentSlideData.interactiveElement,
                      data,
                    },
                  });
                }
              }}
            />
          ) : null}
        </div>
      ) : (
        filterSlideImagesByTemplate(
          currentSlideData.images,
          currentSlideData.templateType,
          SlideTemplateType.GALLERY,
        )?.map((image, index) => (
          <SlideImagePreview key={index}>
            <SlideImageComponent
              key={index}
              image={image}
              currentEditingSlide={currentEditingSlide}
              updateSlide={updateSlide}
              textAlignment={
                currentSlideData.textAlignment || TextAlignment.LEFT
              }
              isSummary={isSummary}
            />
          </SlideImagePreview>
        ))
      )}
    </FlexContainer>
  );
};
